import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import { CheckIcon, CreditCardIcon, GiftIcon, Loader2Icon, SparklesIcon } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { PaymentEntriesTable } from '@/components/shared/payment-entries-table'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  createCustomerPortalSession,
  createSubscriptionCheckoutSession,
  getSubscriptionPackages,
  getUserPaymentEntries,
  getUserPurchasedPackages,
  getUserSubscriptionStatus,
  redeemVoucherCode,
} from '@/lib/api'
import { DEFAULT_TABLE_PAGE_SIZE, TABLE_PAGE_SIZE_OPTIONS } from '@/lib/constants'
import { formatDateTime } from '@/lib/date'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import {
  formatWeeklyAllowance,
  isRecurringPackage,
  isRecurringSubscription,
} from '@/lib/subscriptions'
import type { SubscriptionPackage, UserSubscription } from '@/types/domain'

function formatPrice(cents: number): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`
}

function getEffectivePriceCents(pkg: SubscriptionPackage): number {
  return pkg.effective_price_cents ?? pkg.discount_price_cents ?? pkg.price_cents
}

function getBasePriceCents(pkg: SubscriptionPackage): number {
  return pkg.base_price_cents ?? pkg.discount_price_cents ?? pkg.price_cents
}

function getPackageDisplayPriceCents(pkg: SubscriptionPackage): number {
  return pkg.discount_price_cents ?? pkg.price_cents
}

function hasPackageDiscountPrice(pkg: SubscriptionPackage): boolean {
  return pkg.discount_price_cents !== null && pkg.discount_price_cents < pkg.price_cents
}

function hasAppliedMemberDiscount(pkg: SubscriptionPackage): boolean {
  return Boolean(pkg.applied_discount_id && (pkg.applied_discount_amount_cents ?? 0) > 0)
}

function formatDateOnly(value: string | null | undefined): string {
  if (!value) {
    return '-'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return '-'
  }

  return format(parsed, 'dd.MM.yyyy', { locale: de })
}

function packageStatusLabel(pkg: {
  entry_kind: 'package' | 'manual_debit' | 'voucher'
  billing_mode: 'payment' | 'subscription'
  remaining_credits: number
  is_active: boolean
}): {
  label: string
  variant: 'default' | 'secondary' | 'destructive'
} {
  if (pkg.entry_kind === 'manual_debit') {
    return { label: 'Abgebucht', variant: 'secondary' }
  }

  if (pkg.entry_kind === 'voucher') {
    return pkg.remaining_credits > 0
      ? { label: 'Gutgeschrieben', variant: 'secondary' }
      : { label: 'Aufgebraucht', variant: 'destructive' }
  }

  if (pkg.billing_mode === 'payment') {
    if (pkg.remaining_credits > 0) {
      return { label: 'Guthaben verfügbar', variant: 'secondary' }
    }

    return { label: 'Aufgebraucht', variant: 'destructive' }
  }

  return pkg.is_active
    ? { label: 'Aktiv', variant: 'default' }
    : { label: 'Inaktiv', variant: 'secondary' }
}

const PACKAGE_FEATURES: Record<string, string[]> = {
  single: ['1 Kurseinheit', 'Keine Laufzeit', 'Flexibel einsetzbar'],
  block_10: ['10 Kurseinheiten', 'Keine Laufzeit', 'Flexibel einsetzbar',],
  trial_package: ['3 Kurseinheiten', 'Keine Laufzeit', 'Flexibel einsetzbar', 'Ideal zum Kennenlernen'],
  monthly: ['Bis zu 1 Kurs pro Woche', 'Monatlich kündbar', 'Ideal für regelmäßiges Training'],
  monthly_plus: ['Bis zu 2 Kurse pro Woche', 'Monatlich kündbar', 'Für maximale Trainingsfrequenz'],
  unlimited: [
    'Unbegrenzt viele Kurse pro Woche',
    'Jederzeit monatlich kündbar',
    'Ohne Bindung & Kündigungsfrist',
    'Gesamtes Kursangebot inklusive',
    'Trainiere, wann du willst',
  ],
  personal_training: ['Individuelles Training', 'Individuelle Beratung', 'Individuelle Zeit']
}

type PaymentStep = 'confirm' | 'redirecting'
type AboDetailsTab = 'purchased-packages' | 'payments'

function getPackagePriceLabel(pkg: SubscriptionPackage): string {
  if(pkg.type === 'personal_training'){
    return '/ Einheit'
  }
  
  if (isRecurringPackage(pkg)) {
    return '/ Monat'
  }

  if (pkg.credits_included > 1) {
    return `/ ${pkg.credits_included} Kurse`
  }

  return '/ Kurs'
}

function PackageCard({
  pkg,
  currentSub,
  onSelect,
}: {
  pkg: SubscriptionPackage
  currentSub: UserSubscription | null
  onSelect: (pkg: SubscriptionPackage) => void
}) {
  const isCurrentPlan = currentSub?.package_type === pkg.type && currentSub?.is_active
  const features = PACKAGE_FEATURES[pkg.type] ?? []
  const isPopular = pkg.type === 'monthly_plus'

  return (
    <Card
      className={`relative transition-all hover:shadow-lg ${
        isCurrentPlan
          ? 'border-primary border-2 shadow-primary/10 shadow-lg'
          : 'border-border hover:border-primary/40'
      }`}
    >
      {isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-primary text-primary-foreground shadow-md">Dein aktuelles Paket</Badge>
        </div>
      )}
      {isPopular && !isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge variant="secondary" className="shadow-md">
            <SparklesIcon className="mr-1 h-3 w-3" />
            Beliebt
          </Badge>
        </div>
      )}

      <CardHeader className="pb-2 pt-6 text-center">
        <CardTitle className="text-xl">{pkg.name}</CardTitle>
        <div className="mt-2 space-y-1">
          {hasPackageDiscountPrice(pkg) && (
            <p className="text-sm text-muted-foreground line-through">{formatPrice(pkg.price_cents)}</p>
          )}
          {hasPackageDiscountPrice(pkg) && hasAppliedMemberDiscount(pkg) && (
            <p className="text-sm font-medium">
              Aktionspreis {formatPrice(getPackageDisplayPriceCents(pkg))}
            </p>
          )}
          <p className="text-3xl font-bold text-primary">{formatPrice(getEffectivePriceCents(pkg))}</p>
        </div>
        <p className="text-xs text-muted-foreground">{getPackagePriceLabel(pkg)}</p>
        {hasAppliedMemberDiscount(pkg) && (
          <p className="text-xs font-medium text-primary">
            Persönlicher Rabatt: {pkg.applied_discount_title} (-{formatPrice(pkg.applied_discount_amount_cents ?? 0)})
          </p>
        )}
        {hasPackageDiscountPrice(pkg) && !hasAppliedMemberDiscount(pkg) && (
          <Badge variant="secondary" className="mx-auto w-fit">
            Aktionspreis
          </Badge>
        )}
        {hasPackageDiscountPrice(pkg) && hasAppliedMemberDiscount(pkg) && (
          <p className="text-xs text-muted-foreground">
            Endpreis nach persönlichem Rabatt
          </p>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        <ul className="space-y-2">
          {features.map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-sm">
              <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <Button
          className="w-full"
          variant={isCurrentPlan ? 'outline' : 'default'}
          disabled={isCurrentPlan}
          onClick={() => onSelect(pkg)}
        >
          {isCurrentPlan ? 'Aktuelles Paket' : 'Paket wählen'}
        </Button>
      </CardContent>
    </Card>
  )
}

export function MemberAboPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const handledCheckoutResultRef = useRef<string | null>(null)
  const [selectedPackage, setSelectedPackage] = useState<SubscriptionPackage | null>(null)
  const [paymentStep, setPaymentStep] = useState<PaymentStep>('confirm')
  const [purchasedPackagesPage, setPurchasedPackagesPage] = useState(1)
  const [purchasedPackagesPageSize, setPurchasedPackagesPageSize] = useState(
    DEFAULT_TABLE_PAGE_SIZE,
  )
  const [billingEntriesPage, setBillingEntriesPage] = useState(1)
  const [billingEntriesPageSize, setBillingEntriesPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE)
  const [aboDetailsTab, setAboDetailsTab] = useState<AboDetailsTab>('purchased-packages')
  const [voucherCode, setVoucherCode] = useState('')

  const checkoutResult = searchParams.get('checkout')

  const subscriptionQuery = useQuery({
    queryKey: queryKeys.userSubscription,
    queryFn: getUserSubscriptionStatus,
    retry: false,
  })

  const packagesQuery = useQuery({
    queryKey: queryKeys.subscriptionPackages,
    queryFn: getSubscriptionPackages,
  })

  const purchasedPackagesQuery = useQuery({
    queryKey: queryKeys.userPurchasedPackages,
    queryFn: getUserPurchasedPackages,
  })

  const paymentEntriesQuery = useQuery({
    queryKey: queryKeys.userPaymentEntries,
    queryFn: getUserPaymentEntries,
  })

  useEffect(() => {
    if (!checkoutResult) {
      handledCheckoutResultRef.current = null
      return
    }

    if (handledCheckoutResultRef.current === checkoutResult) {
      return
    }

    handledCheckoutResultRef.current = checkoutResult

    if (checkoutResult === 'success') {
      toast.success('Zahlung abgeschlossen. Das Paket wird jetzt aktiviert.')
      queryClient.invalidateQueries({ queryKey: queryKeys.userSubscription })
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionPackages })
      queryClient.invalidateQueries({ queryKey: queryKeys.userPurchasedPackages })
      queryClient.invalidateQueries({ queryKey: queryKeys.userPaymentEntries })
    } else if (checkoutResult === 'cancelled') {
      toast.info('Checkout wurde abgebrochen.')
    }

    navigate('/abo', { replace: true })
  }, [checkoutResult, navigate, queryClient])

  const checkoutMutation = useMutation({
    mutationFn: (packageType: string) => createSubscriptionCheckoutSession(packageType),
    onSuccess: ({ url }) => {
      window.location.assign(url)
    },
    onError: (error) => {
      setPaymentStep('confirm')
      toast.error(getErrorMessage(error))
    },
  })

  const portalMutation = useMutation({
    mutationFn: createCustomerPortalSession,
    onSuccess: ({ url }) => {
      window.location.assign(url)
    },
    onError: (error) => {
      setPaymentStep('confirm')
      toast.error(getErrorMessage(error))
    },
  })

  const voucherMutation = useMutation({
    mutationFn: (code: string) => redeemVoucherCode(code),
    onSuccess: (result) => {
      setVoucherCode('')
      toast.success(`${result.credits_granted} Kurse wurden deinem Guthaben gutgeschrieben.`)
      queryClient.invalidateQueries({ queryKey: queryKeys.userSubscription })
      queryClient.invalidateQueries({ queryKey: queryKeys.userPurchasedPackages })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const currentSub = subscriptionQuery.data ?? null
  const packages = useMemo(() => packagesQuery.data ?? [], [packagesQuery.data])
  const creditPackages = useMemo(
    () => packages.filter((pkg) => !isRecurringPackage(pkg)),
    [packages],
  )
  const recurringPackages = useMemo(
    () => packages.filter((pkg) => isRecurringPackage(pkg)),
    [packages],
  )
  const purchasedPackages = useMemo(
    () => purchasedPackagesQuery.data ?? [],
    [purchasedPackagesQuery.data],
  )
  const paymentEntries = useMemo(() => paymentEntriesQuery.data ?? [], [paymentEntriesQuery.data])
  const totalPurchasedPackages = purchasedPackages.length
  const totalPaymentEntries = paymentEntries.length
  const purchasedPackagesTotalPages = Math.max(
    1,
    Math.ceil(totalPurchasedPackages / purchasedPackagesPageSize),
  )
  const billingEntriesTotalPages = Math.max(
    1,
    Math.ceil(totalPaymentEntries / billingEntriesPageSize),
  )
  const purchasedPackagesCurrentPage = Math.min(
    purchasedPackagesPage,
    purchasedPackagesTotalPages,
  )
  const billingEntriesCurrentPage = Math.min(billingEntriesPage, billingEntriesTotalPages)
  const paginatedPurchasedPackages = useMemo(() => {
    const from = (purchasedPackagesCurrentPage - 1) * purchasedPackagesPageSize
    return purchasedPackages.slice(from, from + purchasedPackagesPageSize)
  }, [purchasedPackages, purchasedPackagesCurrentPage, purchasedPackagesPageSize])
  const paginatedBillingEntries = useMemo(() => {
    const from = (billingEntriesCurrentPage - 1) * billingEntriesPageSize
    return paymentEntries.slice(from, from + billingEntriesPageSize)
  }, [paymentEntries, billingEntriesCurrentPage, billingEntriesPageSize])

  const currentPlanIsRecurring = isRecurringSubscription(currentSub)
  const hasCreditBalanceOnly = Boolean(
    currentSub && !currentPlanIsRecurring && currentSub.remaining_credits > 0,
  )
  const cancellationPlanned = Boolean(
    currentSub && currentPlanIsRecurring && currentSub.stripe_cancel_at_period_end,
  )

  function isPeriodEndPlanChange(pkg: SubscriptionPackage): boolean {
    if (!currentSub?.is_active) {
      return false
    }

    if (!currentPlanIsRecurring || !isRecurringPackage(pkg)) {
      return false
    }

    return currentSub.package_type !== pkg.type
  }

  const selectedIsPeriodEndPlanChange = selectedPackage
    ? isPeriodEndPlanChange(selectedPackage)
    : false

  function handleSelectPackage(pkg: SubscriptionPackage) {
    setSelectedPackage(pkg)
    setPaymentStep('confirm')
  }

  function handleCloseDialog() {
    if (paymentStep === 'redirecting') return
    setSelectedPackage(null)
    setPaymentStep('confirm')
  }

  function handleOpenCustomerPortal() {
    setPaymentStep('redirecting')
    portalMutation.mutate()
  }

  function handleConfirmPurchase() {
    if (!selectedPackage) return

    setPaymentStep('redirecting')

    if (isPeriodEndPlanChange(selectedPackage)) {
      portalMutation.mutate()
      return
    }

    checkoutMutation.mutate(selectedPackage.type)
  }

  function handleRedeemVoucher(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const normalizedCode = voucherCode.trim()
    if (!normalizedCode) {
      toast.error(getErrorMessage(new Error('VOUCHER_CODE_REQUIRED')))
      return
    }

    voucherMutation.mutate(normalizedCode)
  }

  const redirectPending = paymentStep === 'redirecting'

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Mein Abo</h1>
        <p className="text-sm text-muted-foreground">Verwalte dein Kurspaket und buche Guthaben hinzu.</p>
      </div>

      {subscriptionQuery.isLoading ? (
        <Card>
          <CardContent className="py-6">
            <Skeleton className="h-16 w-full" />
          </CardContent>
        </Card>
      ) : currentSub ? (
        <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-background">
          <CardContent className="space-y-3 py-5">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <CreditCardIcon className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">{currentSub.package_name}</p>
                {currentPlanIsRecurring ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {formatWeeklyAllowance(currentSub)}
                    </p>
                    {currentSub.remaining_credits > 0 && (
                      <p className="text-sm text-muted-foreground">
                        Zusätzliches Guthaben: {currentSub.remaining_credits} Kurse
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Verbleibend: {currentSub.remaining_credits} Kurse
                  </p>
                )}
              </div>
              <Badge
                variant={
                  hasCreditBalanceOnly
                    ? 'secondary'
                    : cancellationPlanned
                      ? 'secondary'
                    : currentSub.is_active
                      ? 'default'
                      : 'destructive'
                }
              >
                {hasCreditBalanceOnly
                  ? 'Guthaben'
                  : cancellationPlanned
                    ? 'Kündigung geplant'
                    : currentSub.is_active
                      ? 'Aktiv'
                      : 'Inaktiv'}
              </Badge>
            </div>
            {cancellationPlanned && currentSub.current_period_end && (
              <p className="text-xs text-amber-600">
                Kündigung zum {format(new Date(currentSub.current_period_end), 'dd.MM.yyyy', { locale: de })}.
                Bis dahin bleibt dein Abo aktiv.
              </p>
            )}
            {currentSub.expires_at && (
              <p className="text-xs text-muted-foreground">
                Gültig bis: {format(new Date(currentSub.expires_at), 'dd.MM.yyyy', { locale: de })}
              </p>
            )}

            {currentPlanIsRecurring && currentSub.is_active && (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={redirectPending || portalMutation.isPending}
                  onClick={handleOpenCustomerPortal}
                >
                  {portalMutation.isPending ? (
                    <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <CreditCardIcon className="mr-2 h-4 w-4" />
                  )}
                  Abo im Kundenportal verwalten
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-muted-foreground/30 border-2 border-dashed">
          <CardContent className="py-6 text-center text-sm text-muted-foreground">
            Du hast kein aktives Abo. Wähle eines der folgenden Pakete.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <GiftIcon className="h-5 w-5 text-primary" />
            Gutschein einlösen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form className="flex flex-col gap-3 md:flex-row" onSubmit={handleRedeemVoucher}>
            <Input
              value={voucherCode}
              onChange={(event) => setVoucherCode(event.target.value)}
              placeholder="Gutschein-Code"
              autoComplete="off"
              className="font-mono uppercase"
              disabled={voucherMutation.isPending}
            />
            <Button
              type="submit"
              disabled={voucherMutation.isPending || voucherCode.trim().length === 0}
              className="md:w-44"
            >
              {voucherMutation.isPending ? (
                <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <GiftIcon className="mr-2 h-4 w-4" />
              )}
              Einlösen
            </Button>
          </form>
        </CardContent>
      </Card>

      <div>
        {packagesQuery.isLoading ? (
          <div className="space-y-8">
            <div>
              <Skeleton className="mb-4 h-6 w-40" />
              <div className="grid gap-4 md:grid-cols-3">
                <Skeleton className="h-64" />
                <Skeleton className="h-64" />
              </div>
            </div>
            <div>
              <Skeleton className="mb-4 h-6 w-28" />
              <div className="grid gap-4 md:grid-cols-3">
                <Skeleton className="h-64" />
                <Skeleton className="h-64" />
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {creditPackages.length > 0 && (
              <div>
                <h3 className="mb-4 text-base font-semibold">Pakete</h3>
                <div className="grid gap-4 md:grid-cols-3">
                  {creditPackages.map((pkg) => (
                    <PackageCard key={pkg.id} pkg={pkg} currentSub={currentSub} onSelect={handleSelectPackage} />
                  ))}
                </div>
              </div>
            )}

            {recurringPackages.length > 0 && (
              <div>
                <h3 className="mb-4 text-base font-semibold">Abos</h3>
                <div className="grid gap-4 md:grid-cols-3">
                  {recurringPackages.map((pkg) => (
                    <PackageCard key={pkg.id} pkg={pkg} currentSub={currentSub} onSelect={handleSelectPackage} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <Card className="flex min-h-0 flex-col overflow-hidden">
        <Tabs
          value={aboDetailsTab}
          onValueChange={(value) => setAboDetailsTab(value as AboDetailsTab)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <CardHeader className="flex shrink-0 flex-col gap-3">
            <CardTitle className="text-lg">Pakete und Zahlungen</CardTitle>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <TabsList className="h-auto w-full justify-start gap-1 p-1 md:w-auto">
                <TabsTrigger value="purchased-packages">Gekaufte Pakete</TabsTrigger>
                <TabsTrigger value="payments">Zahlungen</TabsTrigger>
              </TabsList>

              {aboDetailsTab === 'purchased-packages' ? (
                <Select
                  value={String(purchasedPackagesPageSize)}
                  onValueChange={(value) => {
                    setPurchasedPackagesPageSize(Number(value))
                    setPurchasedPackagesPage(1)
                  }}
                >
                  <SelectTrigger className="w-full bg-card md:w-[9rem]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TABLE_PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size} pro Seite
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Select
                  value={String(billingEntriesPageSize)}
                  onValueChange={(value) => {
                    setBillingEntriesPageSize(Number(value))
                    setBillingEntriesPage(1)
                  }}
                >
                  <SelectTrigger className="w-full bg-card md:w-[9rem]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TABLE_PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size} pro Seite
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardHeader>

          <CardContent className="min-h-0 flex-1 overflow-hidden">
            <TabsContent value="purchased-packages" className="mt-0 flex min-h-0 flex-col">
              {purchasedPackagesQuery.isLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : totalPurchasedPackages === 0 ? (
                <p className="text-sm text-muted-foreground">Keine Pakete vorhanden.</p>
              ) : (
                <div className="flex min-h-0 flex-1 flex-col gap-4">
                  <Table containerClassName="min-h-0 flex-1 max-h-none">
                      <TableHeader>
                      <TableRow>
                        <TableHead>Paket</TableHead>
                        <TableHead>Typ</TableHead>
                        <TableHead>Info</TableHead>
                        <TableHead>Gekauft am</TableHead>
                        <TableHead>Gültig bis</TableHead>
                        <TableHead>Gekaufte Kurse</TableHead>
                        <TableHead>Abbuchungen</TableHead>
                        <TableHead>Verbleibende Kurse</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedPurchasedPackages.map((pkg) => {
                        const status = packageStatusLabel(pkg)

                        return (
                          <TableRow key={pkg.id}>
                            <TableCell>
                              {pkg.entry_kind === 'voucher'
                                ? 'Gutschein'
                                : pkg.package_type === 'manual_credit'
                                ? 'Manuelle Buchung'
                                : pkg.package_name}
                            </TableCell>
                            <TableCell>
                              {pkg.entry_kind === 'voucher'
                                ? 'Gutschein'
                                : pkg.billing_mode === 'subscription'
                                  ? 'Abo'
                                  : 'Guthaben'}
                            </TableCell>
                            <TableCell>
                              {pkg.entry_kind === 'voucher'
                                ? (pkg.manual_reason ?? '-')
                                : pkg.package_type === 'manual_credit'
                                  ? (pkg.manual_reason ?? '-')
                                  : '-'}
                            </TableCell>
                            <TableCell>{formatDateTime(pkg.created_at)}</TableCell>
                            <TableCell>{formatDateOnly(pkg.expires_at)}</TableCell>
                            <TableCell>{pkg.purchased_credits ?? '-'}</TableCell>
                            <TableCell>{pkg.debited_credits > 0 ? pkg.debited_credits : '-'}</TableCell>
                            <TableCell>
                              {pkg.entry_kind === 'manual_debit' ||
                              pkg.billing_mode === 'subscription'
                                ? '-'
                                : pkg.remaining_credits}
                            </TableCell>
                            <TableCell>
                              <Badge variant={status.variant}>{status.label}</Badge>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                  <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                    <p>
                      {(purchasedPackagesCurrentPage - 1) * purchasedPackagesPageSize + 1} -{' '}
                      {Math.min(
                        (purchasedPackagesCurrentPage - 1) * purchasedPackagesPageSize +
                          paginatedPurchasedPackages.length,
                        totalPurchasedPackages,
                      )}{' '}
                      von {totalPurchasedPackages}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setPurchasedPackagesPage(Math.max(1, purchasedPackagesCurrentPage - 1))
                        }
                        disabled={purchasedPackagesCurrentPage <= 1}
                      >
                        Zurück
                      </Button>
                      <span>
                        Seite {purchasedPackagesCurrentPage} / {purchasedPackagesTotalPages}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setPurchasedPackagesPage(
                            Math.min(purchasedPackagesTotalPages, purchasedPackagesCurrentPage + 1),
                          )
                        }
                        disabled={purchasedPackagesCurrentPage >= purchasedPackagesTotalPages}
                      >
                        Weiter
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="payments" className="mt-0 flex min-h-0 flex-col">
              {paymentEntriesQuery.isLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : totalPaymentEntries === 0 ? (
                <p className="text-sm text-muted-foreground">Keine Zahlungen vorhanden.</p>
              ) : (
                <div className="flex min-h-0 flex-1 flex-col gap-4">
                  <PaymentEntriesTable entries={paginatedBillingEntries} />
                  <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                    <p>
                      {(billingEntriesCurrentPage - 1) * billingEntriesPageSize + 1} -{' '}
                      {Math.min(
                        (billingEntriesCurrentPage - 1) * billingEntriesPageSize +
                          paginatedBillingEntries.length,
                        totalPaymentEntries,
                      )}{' '}
                      von {totalPaymentEntries}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setBillingEntriesPage(Math.max(1, billingEntriesCurrentPage - 1))
                        }
                        disabled={billingEntriesCurrentPage <= 1}
                      >
                        Zurück
                      </Button>
                      <span>
                        Seite {billingEntriesCurrentPage} / {billingEntriesTotalPages}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setBillingEntriesPage(
                            Math.min(billingEntriesTotalPages, billingEntriesCurrentPage + 1),
                          )
                        }
                        disabled={billingEntriesCurrentPage >= billingEntriesTotalPages}
                      >
                        Weiter
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>

      <Dialog open={selectedPackage !== null} onOpenChange={(open) => !open && handleCloseDialog()}>
        <DialogContent className="sm:max-w-md">
          {paymentStep === 'confirm' && selectedPackage && (
            <>
              <DialogHeader>
                <DialogTitle>Paket buchen</DialogTitle>
                <DialogDescription>
                  Du bist dabei, das folgende Kurspaket zu buchen:
                </DialogDescription>
              </DialogHeader>

              <div className="my-2 space-y-3 rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{selectedPackage.name}</span>
                  <div className="text-right">
                    {hasPackageDiscountPrice(selectedPackage) && (
                      <p className="text-xs text-muted-foreground line-through">
                        {formatPrice(selectedPackage.price_cents)}
                      </p>
                    )}
                    {hasPackageDiscountPrice(selectedPackage) && (
                      <p className="text-xs font-medium">
                        Aktionspreis {formatPrice(getPackageDisplayPriceCents(selectedPackage))}
                      </p>
                    )}
                    {hasAppliedMemberDiscount(selectedPackage) && (
                      <>
                        <p className="text-xs font-medium text-primary">
                          {selectedPackage.applied_discount_title}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Persönlicher Rabatt auf {formatPrice(getBasePriceCents(selectedPackage))}:{' '}
                          -{formatPrice(selectedPackage.applied_discount_amount_cents ?? 0)}
                        </p>
                      </>
                    )}
                    <span className="text-xl font-bold text-primary">
                      {formatPrice(getEffectivePriceCents(selectedPackage))}
                    </span>
                  </div>
                </div>
                <ul className="space-y-1">
                  {(PACKAGE_FEATURES[selectedPackage.type] ?? []).map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckIcon className="h-3 w-3 shrink-0 text-primary" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {selectedIsPeriodEndPlanChange ? (
                  <p className="mt-2 text-xs text-amber-600">
                    Wechsel zwischen Monatsabos laufen über das Kundenportal.
                  </p>
                ) : currentSub?.is_active ? (
                  <p className="mt-2 text-xs text-amber-600">
                    Die Kurse werden deinem Konto sofort gutgeschrieben.
                  </p>
                ) : null}
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={handleCloseDialog}>
                  Abbrechen
                </Button>
                <Button onClick={handleConfirmPurchase}>
                  <CreditCardIcon className="mr-2 h-4 w-4" />
                  {selectedIsPeriodEndPlanChange ? 'Im Kundenportal fortfahren' : 'Jetzt bezahlen'}
                </Button>
              </DialogFooter>
            </>
          )}

          {paymentStep === 'redirecting' && (
            <div className="flex flex-col items-center justify-center gap-4 py-12">
              <Loader2Icon className="h-12 w-12 animate-spin text-primary" />
              <p className="text-lg font-semibold">Weiterleitung...</p>
              <p className="text-center text-sm text-muted-foreground">
                Du wirst zum sicheren Zahlungsbereich von Stripe weitergeleitet.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
